"use strict";

/**
 * Client-side speed helpers for the SM cockpit (perf pass, Oct 2026). Pure
 * logic only — App.jsx wires these to React state — so every behaviour here
 * is unit-tested under plain `node --test` (speed.test.js):
 *
 *  - optimistic Plan moves with revert + read-back reconcile
 *  - patching the report state from a waiver/remind response's `readiness`
 *  - the weekly-report per-week cache
 *  - the NCR screen's visibility-aware poll lifecycle
 *  - the "+ Schedule" form's validation and request body
 */

import { reportEnvelopeKey } from "./reportPeriod.js";

// ---------------------------------------------------------------------------
// Plan: optimistic move / reconcile / revert
// ---------------------------------------------------------------------------

function sameItem(row, code, scheduleRef) {
  return Boolean(row) && row.code === code && row.scheduleRef === scheduleRef;
}

/**
 * The Plan envelope as it will look once `tile` is dropped on
 * `{ assignedTo, plannedDate }`. A tile already on the grid is replaced in
 * place; an unbooked/backlog row gains a date and leaves `unbooked` and the
 * backlog page (the server read-back does the same). Never mutates `envelope`.
 */
export function applyOptimisticMove(envelope, tile, { assignedTo, plannedDate } = {}) {
  if (!envelope || !tile) return envelope;
  const lower = (v) => String(v || "").trim().toLowerCase();
  const patch = {};
  if (assignedTo !== undefined) patch.assignedTo = lower(assignedTo);
  if (plannedDate !== undefined) patch.plannedDate = plannedDate;
  const items = envelope.items || [];
  const inItems = items.some((r) => sameItem(r, tile.code, tile.scheduleRef));
  if (inItems) {
    return { ...envelope, items: items.map((r) => (sameItem(r, tile.code, tile.scheduleRef) ? { ...r, ...patch } : r)) };
  }
  const source = tile.row || {};
  const moved = { ...source, code: tile.code, scheduleRef: tile.scheduleRef, ...patch };
  const unbooked = envelope.unbooked || [];
  const backlog = envelope.backlog || [];
  const nextBacklog = backlog.filter((r) => !(r.kind !== "reinspection" && sameItem(r, tile.code, tile.scheduleRef)));
  const removedFromBacklog = backlog.length - nextBacklog.length;
  const next = {
    ...envelope,
    items: [...items, moved],
    unbooked: unbooked.filter((r) => !sameItem(r, tile.code, tile.scheduleRef)),
    backlog: nextBacklog,
  };
  if (removedFromBacklog && typeof envelope.backlogTotal === "number") {
    next.backlogTotal = Math.max(0, envelope.backlogTotal - removedFromBacklog);
  }
  return next;
}

/** The Plan envelope with a brand-new schedule row (the "+ Schedule" form) on the grid. */
export function applyOptimisticCreate(envelope, code, item) {
  if (!envelope) return envelope;
  const row = {
    scheduleId: 0,
    code,
    scheduleStatus: "Planned",
    linkedInspectionNumber: 0,
    sourceInspectionNumber: 0,
    reminderSentDate: "",
    importedAtUtc: "",
    importedBy: "",
    etag: "",
    plannedTime: "",
    witnessParties: "",
    notes: "",
    ...item,
    assignedTo: String(item.assignedTo || "").trim().toLowerCase(),
  };
  return { ...envelope, items: [...(envelope.items || []), row] };
}

/**
 * Folds the upsert read-back for ONE row into `envelope` (replace by project
 * + scheduleRef, or add when absent). Returns `null` when there is no usable
 * read-back, so the caller keeps its optimistic state and relies on the
 * background refetch.
 */
export function reconcileUpsertReadback(envelope, code, scheduleRef, readback) {
  if (!envelope || !readback || typeof readback !== "object") return null;
  const row = { ...readback, code, scheduleRef: readback.scheduleRef || scheduleRef };
  if (row.assignedTo !== undefined) row.assignedTo = String(row.assignedTo || "").toLowerCase();
  const items = envelope.items || [];
  const exists = items.some((r) => sameItem(r, code, row.scheduleRef));
  return {
    ...envelope,
    items: exists ? items.map((r) => (sameItem(r, code, row.scheduleRef) ? { ...r, ...row } : r)) : [...items, row],
    unbooked: (envelope.unbooked || []).filter((r) => !sameItem(r, code, row.scheduleRef)),
  };
}

function keyOf(code, scheduleRef) {
  return `${code}|${scheduleRef}`;
}

/** The pre-write state of one row across the Plan collections, for a row-level revert. */
function captureRow(env, code, scheduleRef) {
  if (!env) return { item: null, unbooked: null, backlog: [] };
  const find = (list) => (list || []).find((r) => sameItem(r, code, scheduleRef)) || null;
  return {
    item: find(env.items),
    unbooked: find(env.unbooked),
    backlog: (env.backlog || []).filter((r) => r.kind !== "reinspection" && sameItem(r, code, scheduleRef)),
  };
}

function restoreList(list, code, scheduleRef, originals, isBacklog) {
  const current = list || [];
  const match = (r) => sameItem(r, code, scheduleRef) && (!isBacklog || r.kind !== "reinspection");
  if (!originals.length) return current.filter((r) => !match(r));
  const present = current.some(match);
  if (present) {
    let i = 0;
    return current.map((r) => (match(r) ? originals[Math.min(i++, originals.length - 1)] : r));
  }
  return [...current, ...originals];
}

/** Undo ONE row's optimistic change in `env` (never a whole-envelope snapshot). */
function restoreRow(env, code, scheduleRef, orig) {
  if (!env) return env;
  const backlog = restoreList(env.backlog, code, scheduleRef, orig.backlog, true);
  const next = {
    ...env,
    items: restoreList(env.items, code, scheduleRef, orig.item ? [orig.item] : [], false),
    unbooked: restoreList(env.unbooked, code, scheduleRef, orig.unbooked ? [orig.unbooked] : [], false),
    backlog,
  };
  if (typeof env.backlogTotal === "number") {
    next.backlogTotal = Math.max(0, env.backlogTotal + (backlog.length - (env.backlog || []).length));
  }
  return next;
}

/**
 * Optimistic-write controller for Plan. Pending writes live in a map; every
 * view of the week is "server data + pending moves":
 *  - `begin(week, { code, scheduleRef, apply })` stamps a write epoch (a
 *    slow earlier prefetch can never commit over it), drops the week's cache
 *    and session snapshot, records the row's original state, and shows
 *    `apply(current)` at once;
 *  - `overlay(week, serverEnv)` re-applies the still-pending moves on top of
 *    data a refetch just returned — use it before rendering any refetch;
 *  - `confirm` folds the read-back into the CURRENT envelope (unless a later
 *    move of the same row is pending) and writes the cache/snapshot only when
 *    nothing else is pending for that week;
 *  - `revert` undoes only that row in the CURRENT envelope.
 */
export function createPlanOptimistic({ getEnvelope, setEnvelope, cache, gate, onInFlight }) {
  const pending = new Map(); // id -> txn
  let nextId = 0;

  const others = (txn) => [...pending.values()].filter((t) => t.id !== txn.id);
  const sameRowPending = (txn) => others(txn).some((t) => t.week === txn.week && keyOf(t.code, t.scheduleRef) === keyOf(txn.code, txn.scheduleRef));
  const hasPending = (week) => [...pending.values()].some((t) => t.week === week);

  return {
    hasPending,
    overlay(week, serverEnv) {
      if (!serverEnv) return serverEnv;
      let out = serverEnv;
      for (const t of pending.values()) if (t.week === week) out = t.apply(out);
      return out;
    },
    begin(week, { code, scheduleRef, apply }) {
      const current = getEnvelope();
      const txn = { id: ++nextId, week, code, scheduleRef, apply, orig: captureRow(current, code, scheduleRef), stamp: gate.startWrite(week), settled: false };
      cache.delete(week);
      if (onInFlight) onInFlight(1);
      pending.set(txn.id, txn);
      setEnvelope(apply(current));
      return txn;
    },
    confirm(txn, reconciled) {
      if (txn.settled) return;
      txn.settled = true;
      pending.delete(txn.id);
      if (onInFlight) onInFlight(-1);
      const current = getEnvelope();
      if (!current || current.weekStart !== txn.week) return; // screen moved on; the next fetch fills the cache
      const next = reconciled && !sameRowPending(txn) ? reconciled(current) || current : current;
      if (next !== current) setEnvelope(next);
      if (!hasPending(txn.week) && gate.tryCommit(txn.week, txn.stamp)) {
        cache.set(txn.week, next);
      }
    },
    revert(txn) {
      if (txn.settled) return;
      txn.settled = true;
      pending.delete(txn.id);
      if (onInFlight) onInFlight(-1);
      const current = getEnvelope();
      if (!current || current.weekStart !== txn.week || sameRowPending(txn)) return;
      setEnvelope(restoreRow(current, txn.code, txn.scheduleRef, txn.orig));
    },
  };
}

// ---------------------------------------------------------------------------
// Reports: readiness patching + week cache
// ---------------------------------------------------------------------------

/**
 * A copy of a `GET /api/cockpit/reports` envelope with `code`'s readiness
 * replaced by the one a waiver/remind response carried. Returns `null` (caller
 * falls back to a reload) when the response has no readiness, the project is
 * not in the envelope, or the envelope is for a different week.
 */
export function patchReportReadiness(envelope, code, weekStart, readiness) {
  if (!envelope || !readiness || typeof readiness !== "object" || Array.isArray(readiness)) return null;
  // `weekStart` is the VIEW key: a week's Monday, or "start..end" for a custom
  // period (SPEC-CIS-REPORT-CUSTOM-PERIOD-001 section 4.2) -- a period's
  // readiness never lands on a week's envelope, nor the reverse.
  if (weekStart && reportEnvelopeKey(envelope) && reportEnvelopeKey(envelope) !== weekStart) return null;
  const projects = envelope.projects || [];
  if (!projects.some((p) => p.code === code)) return null;
  return { ...envelope, projects: projects.map((p) => (p.code === code ? { ...p, readiness } : p)) };
}

/**
 * Patches readiness against the CURRENT report state (never a render-time
 * closure). `shown`/`shownWeek` = the envelope on screen and its week;
 * `cache` = the per-week map. Updates the cache entry for `week` always, and
 * the displayed envelope only when `week` is the one on screen. `supersedeLoads`
 * tells the caller to drop older in-flight report loads (only when the
 * patched week is the displayed one, so another week's load is never dropped).
 */
export function applyReadinessUpdate({ shown, shownWeek, cache, week, code, readiness }) {
  const cached = cache.get(week);
  const patchedCache = cached ? patchReportReadiness(cached, code, week, readiness) : null;
  if (patchedCache) cache.set(week, patchedCache);
  if (shownWeek !== week) {
    const valid = Boolean(readiness) && typeof readiness === "object" && !Array.isArray(readiness);
    return { applied: valid, shown, supersedeLoads: false };
  }
  const patched = patchReportReadiness(shown, code, week, readiness);
  if (!patched) return { applied: false, shown, supersedeLoads: false };
  cache.set(week, patched);
  return { applied: true, shown: patched, supersedeLoads: true };
}

/** A week-wide refetch always refreshes its own week's cache, but may only render if that week is the one on screen. */
export function decideWeekCommit({ forWeek, shownWeek }) {
  return { cache: true, show: Boolean(shownWeek) && forWeek === shownWeek };
}

/**
 * What a Plan response may do: "drop" (a superseded navigation/write load),
 * "render" (replace the screen) or "cache-only" (prefetch, or a quiet refetch
 * whose week is no longer the one on screen). Quiet refetches never take part
 * in the navigation generation, so they can neither be superseded by nor
 * supersede a real week click.
 */
export function planResponseDisposition({ write, quiet, myGen, currentGen, forWeek, shownWeek, errorWeek = null }) {
  // A success for the week the error screen is showing (a background prefetch
  // or a later retry) must clear that error, never leave it standing.
  if (!write) return forWeek === shownWeek && forWeek === errorWeek ? "render" : "cache-only";
  if (quiet) return forWeek === shownWeek ? "render" : "cache-only";
  if (myGen !== currentGen) return "drop";
  return "render";
}

/** A background (quiet) NCR load only stays quiet while there is data on screen; otherwise failure must surface. */
export function isQuietNcrRefresh({ quiet, hasData }) {
  return Boolean(quiet) && Boolean(hasData);
}

/** Generation counter: only the latest call's response may be applied; `supersede` drops everything in flight. */
export function createGenerationGuard() {
  let gen = 0;
  return {
    next: () => ++gen,
    supersede: () => ++gen,
    isCurrent: (g) => g === gen,
  };
}

/** In-memory per-session cache of report-page envelopes keyed by week (never cross-tab). */
export function createReportWeekCache() {
  const map = new Map();
  return {
    get: (week) => map.get(week),
    has: (week) => map.has(week),
    set: (week, envelope) => {
      map.set(week, envelope);
    },
    delete: (week) => {
      map.delete(week);
    },
    clear: () => map.clear(),
    size: () => map.size,
  };
}

// ---------------------------------------------------------------------------
// NCR: poll lifecycle
// ---------------------------------------------------------------------------

export const NCR_POLL_INTERVAL_MS = 30000;

/**
 * Keeps the NCR screen fresh: refetches on window focus and every
 * `intervalMs` while the document is visible; the timer is cleared while the
 * tab is hidden and restarted (with an immediate refetch) when it becomes
 * visible again. The caller starts this ONLY while the NCR screen is showing
 * and calls the returned `stop()` when leaving it, so no other screen ever
 * polls. Overlapping refetches are skipped. Everything environmental is
 * injected so the lifecycle is testable without a DOM.
 */
export function startNcrPolling({
  doc,
  win,
  refetch,
  intervalMs = NCR_POLL_INTERVAL_MS,
  setIntervalFn = setInterval,
  clearIntervalFn = clearInterval,
}) {
  let timer = null;
  let inFlight = false;
  let stopped = false;

  const visible = () => !doc || doc.visibilityState !== "hidden";

  async function tick() {
    if (stopped || inFlight || !visible()) return;
    inFlight = true;
    try {
      await refetch();
    } catch {
      // A failed quiet refetch leaves the current data on screen.
    } finally {
      inFlight = false;
    }
  }

  function startTimer() {
    if (timer !== null || stopped) return;
    timer = setIntervalFn(tick, intervalMs);
  }

  function stopTimer() {
    if (timer === null) return;
    clearIntervalFn(timer);
    timer = null;
  }

  function onVisibility() {
    if (visible()) {
      startTimer();
      tick();
    } else {
      stopTimer();
    }
  }

  function onFocus() {
    tick();
  }

  if (visible()) startTimer();
  if (doc && doc.addEventListener) doc.addEventListener("visibilitychange", onVisibility);
  if (win && win.addEventListener) win.addEventListener("focus", onFocus);

  return {
    stop() {
      stopped = true;
      stopTimer();
      if (doc && doc.removeEventListener) doc.removeEventListener("visibilitychange", onVisibility);
      if (win && win.removeEventListener) win.removeEventListener("focus", onFocus);
    },
    isRunning: () => timer !== null,
  };
}

// ---------------------------------------------------------------------------
// "+ Schedule" form
// ---------------------------------------------------------------------------

// Mirrors azure-functions/cis/shared/validation.js (validateScheduleItem):
// CATEGORIES / INSPECTION_TYPES enums, MAX_TEXT 255, plannedTime 24h HH:MM,
// plannedDate yyyy-MM-dd; scheduleRef/title/category/stage/inspectionType/
// plannedDate are required, plannedTime/witnessParties/assignedTo/notes optional.
export const SCHEDULE_CATEGORIES = Object.freeze([
  "Block Assembly",
  "Hull Erection",
  "Hull Outfitting",
  "Machinery Outfitting",
  "Electrical Outfitting",
  "Painting",
  "Sea Trial",
  "Others",
]);
export const SCHEDULE_INSPECTION_TYPES = Object.freeze(["Hold Point", "Witness Point", "Review", "Monitor"]);
const SCHEDULE_MAX_TEXT = 255;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isRealDate(value) {
  if (!DATE_RE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

function randomHex8() {
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const bytes = new Uint8Array(4);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  let out = "";
  for (let i = 0; i < 8; i += 1) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

/** `SM-<yyyyMMdd>-<8 hex>` — the same family the pinned-booking drop already mints. */
export function newScheduleRef(now = new Date(), hex8 = randomHex8()) {
  return `SM-${now.toISOString().slice(0, 10).replace(/-/g, "")}-${hex8}`;
}

export function defaultScheduleForm({ code = "", today } = {}) {
  return {
    code,
    title: "",
    category: "",
    stage: "",
    inspectionType: "Witness Point",
    plannedDate: today || "",
    plannedTime: "",
    assignedTo: "",
  };
}

/** Inspectors on `code`'s roster, from Plan's lanes (a lane's `teams` lists the projects it is labelled with). */
export function rosterForProject(lanes, code) {
  return (lanes || [])
    .filter((lane) => lane.upn && (lane.rosterCodes || lane.teams || []).includes(code))
    .map((lane) => ({ upn: lane.upn, name: lane.name || lane.upn }));
}

/**
 * Validates the form. Returns `{ ok: true, item }` where `item` is exactly the
 * body the existing upsert accepts, or `{ ok: false, errors: { field: msg } }`.
 */
export function validateScheduleForm(values, { projectCodes = [], roster = null, scheduleRef } = {}) {
  const errors = {};
  const v = values || {};
  const text = (x) => (typeof x === "string" ? x.trim() : "");
  const title = text(v.title);
  const stage = text(v.stage);

  if (!text(v.code)) errors.code = "Choose a project";
  else if (projectCodes.length && !projectCodes.includes(v.code)) errors.code = "Choose one of the selected projects";

  if (!title) errors.title = "Enter what is being inspected";
  else if (title.length > SCHEDULE_MAX_TEXT) errors.title = `At most ${SCHEDULE_MAX_TEXT} characters`;

  if (!SCHEDULE_CATEGORIES.includes(v.category)) errors.category = "Choose a trade";

  if (!stage) errors.stage = "Enter the stage";
  else if (stage.length > SCHEDULE_MAX_TEXT) errors.stage = `At most ${SCHEDULE_MAX_TEXT} characters`;

  if (!SCHEDULE_INSPECTION_TYPES.includes(v.inspectionType)) errors.inspectionType = "Choose a type";

  if (!isRealDate(text(v.plannedDate))) errors.plannedDate = "Choose a day";

  const time = text(v.plannedTime);
  if (time && !TIME_RE.test(time)) errors.plannedTime = "Use 24-hour HH:MM, or leave empty";

  const assignedTo = text(v.assignedTo).toLowerCase();
  if (assignedTo && roster && !roster.some((r) => r.upn.toLowerCase() === assignedTo)) {
    errors.assignedTo = "Not on this project's roster";
  }

  if (Object.keys(errors).length) return { ok: false, errors };

  const item = {
    scheduleRef: scheduleRef || newScheduleRef(),
    title,
    category: v.category,
    stage,
    inspectionType: v.inspectionType,
    plannedDate: text(v.plannedDate),
    witnessParties: "",
    assignedTo,
    notes: "",
  };
  if (time) item.plannedTime = time;
  return { ok: true, item };
}
